// lib/koi-pond-v3/assets/skyDome.ts
//
// T7.6h — inverted sphere (BackSide rendering) painting a moonlit
// night sky with a hemispherical glow concentrated near the moon's
// world direction. Without this the upper half of the scene reads as
// solid #000 void with the moon disc pasted on top. Real moonlit
// scenes have measurable ambient sky luminance that brightens near
// the moon and fades toward the opposite horizon.
//
// Geometry: SphereGeometry(50, 32, 16) centered at origin, BackSide
// so the sphere wraps the visible scene from inside. The sphere is
// large enough that scene geometry never intersects it, small enough
// to stay inside the camera's far plane.
//
// Shader: compute the sky direction (sphere surface position normalized),
// dot with moon direction → bright near moon (small angle = high dot),
// dark far from moon (large angle = low dot, possibly negative).
// Falls off as exp(-x²) to give a hemispherical glow gradient rather
// than a sharp circle.

import {
  SphereGeometry,
  Mesh,
  ShaderMaterial,
  BackSide,
  Vector3,
} from 'three';
import { paletteVec3 } from '../palette';

export function makeSkyDome(moonDir: Vector3): {
  mesh: Mesh;
  uniforms: { uMoonWorldDir: { value: Vector3 } };
} {
  const geom = new SphereGeometry(50, 32, 16);

  const uniforms = {
    uMoonWorldDir: { value: moonDir.clone() },
  };

  const material = new ShaderMaterial({
    vertexShader: `
      varying vec3 vWorldPos;
      void main() {
        vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      varying vec3 vWorldPos;
      uniform vec3 uMoonWorldDir;

      void main() {
        // Sky direction = from sphere center (origin) to fragment world position.
        // Sphere is centered at origin so vWorldPos itself, normalized, IS the
        // sky direction at this fragment.
        vec3 skyDir = normalize(vWorldPos);
        vec3 moonDir = normalize(uMoonWorldDir);

        // Dot product with moon direction: 1.0 right at moon, falls to
        // -1.0 at antipode. Clamp to [0, 1] so opposite hemisphere is
        // uniformly deep-dark (no negative-contribution artifacts).
        float moonAffinity = max(0.0, dot(skyDir, moonDir));

        // Gaussian-style falloff. exp(-(1-affinity)²*5) gives a soft
        // hemispherical glow with maximum near the moon. Tunable: lower
        // multiplier (e.g., 3) → broader/softer glow; higher (e.g., 10)
        // → tighter glow.
        float glow = exp(-pow(1.0 - moonAffinity, 2.0) * 5.0);

        // Sky base color: very dark teal-black matching the deep water
        // tone so horizon line between water and sky reads as continuous.
        // Glow color: warmer dim cream tinted toward the moon halo.
        vec3 base = ${paletteVec3('deepWater')};
        vec3 glowColor = vec3(0.08, 0.09, 0.12); // dim cool gray, slightly cooler than moon halo

        vec3 color = mix(base, glowColor, glow * 0.85);

        gl_FragColor = vec4(color, 1.0);
      }
    `,
    side: BackSide,
    depthWrite: false, // sky doesn't occlude scene geometry
  });

  const mesh = new Mesh(geom, material);
  // renderOrder negative so sky renders BEFORE everything else,
  // becoming the effective backdrop. Scene geometry composes over it.
  mesh.renderOrder = -1;
  // Disable frustum culling — sphere is big enough that culling
  // wouldn't miss anyway, but no harm in being explicit.
  mesh.frustumCulled = false;

  return { mesh, uniforms };
}
